import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FileText, Loader2, AlertTriangle, CheckCircle2, ReceiptText, FileSpreadsheet,
  Eye, Plus, Trash2, Landmark, RefreshCw, Filter, Save, BadgeCheck, CalendarDays, Globe2, RotateCw,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { api, apiErrorMessage } from "@/lib/api";
import { PdfViewer } from "./PdfViewer";

const NONE = "__none__";
const CLASE = {
  pendiente: { label: "Pendiente / Revisar", cls: "border-rose-200 bg-rose-50 text-rose-700", icon: AlertTriangle },
  retencion: { label: "Con retención (IRPF)", cls: "border-amber-200 bg-amber-50 text-amber-700", icon: ReceiptText },
  holded: { label: "Holded OK", cls: "border-emerald-200 bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
};
const INV_FIELDS = [
  { col: "Num factura", label: "Nº factura" },
  { col: "Fecha dd/mm/yyyy", label: "Fecha (dd/mm/yyyy)" },
  { col: "Nombre del contacto", label: "Proveedor (contacto)" },
  { col: "NIF", label: "NIF" },
];
const COUNTRY_ORDER = ["España", "Portugal", "Italia", "Marruecos", "Francia", "Reino Unido", "Estados Unidos", "Otros", "Sin país"];

const blankRow = (cols, inv, pais) => {
  const r = Object.fromEntries(cols.map((c) => [c, ""]));
  Object.assign(r, inv, { "País": pais, "Unidades": "1", "IVA %": "0", "Retención %": "0", "Inv. Suj. Pasivo (1/0)": "0" });
  return r;
};

function InvoiceReviewer({ item, cuentas, paises, columns, onClose, onSaved }) {
  const cols = columns || [];
  const [rows, setRows] = useState(() => item.lines.map((l) => ({ ...l.data })));
  const [pais, setPais] = useState(item.pais || "España");
  const [revisada, setRevisada] = useState(!!item.revisada);
  const [pdfData, setPdfData] = useState(null);
  const [pdfErr, setPdfErr] = useState(false);

  const firstRow = item.lines[0]?.data || {};
  const [inv, setInv] = useState(() =>
    Object.fromEntries(INV_FIELDS.map((f) => [f.col, firstRow[f.col] || ""]))
  );

  useEffect(() => {
    let cancelled = false;
    if (item.file_id) {
      api.get(`/contabilidad/pdf/${item.file_id}`, { responseType: "arraybuffer" })
        .then((res) => { if (!cancelled) setPdfData(new Uint8Array(res.data)); })
        .catch(() => { if (!cancelled) setPdfErr(true); });
    } else setPdfErr(true);
    return () => { cancelled = true; };
  }, [item.file_id]);

  const setLine = (idx, col, val) => setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, [col]: val } : r)));
  const setCuenta = (idx, num) => setRows((rs) => rs.map((r, i) => {
    if (i !== idx) return r;
    const c = cuentas.find((x) => x.numero === num);
    return { ...r, "Num. Cuenta de gasto": num === NONE ? "" : num, "Nombre cuenta de gasto": c ? c.nombre : "" };
  }));
  const addLine = () => setRows((rs) => [...rs, blankRow(cols, inv, pais)]);
  const removeLine = (idx) => setRows((rs) => rs.filter((_, i) => i !== idx));

  const saveMut = useMutation({
    mutationFn: async () => {
      const finalRows = rows.map((r) => ({ ...r, ...inv, "País": pais }));
      return (await api.patch("/contabilidad/revision/factura", {
        owner_kind: item.owner_kind, owner_id: item.owner_id, factura_id: item.factura_id,
        rows: finalRows, revisada,
      })).data;
    },
    onSuccess: () => { toast.success("Factura guardada"); onSaved(); },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo guardar")),
  });

  const lineIssues = (idx) => {
    const r = rows[idx];
    const out = [];
    if (!String(r["Num. Cuenta de gasto"] || "").trim()) out.push("Sin cuenta de gasto");
    if (!parseFloat(String(r["Precio unidad"] || "0").replace(",", "."))) out.push("Importe = 0");
    return out;
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl w-[96vw] p-0 gap-0" data-testid="reviewer-dialog">
        <DialogHeader className="border-b px-5 py-3">
          <DialogTitle className="flex flex-wrap items-center gap-2 text-base">
            <FileText className="h-4 w-4 text-primary" /> {item.filename}
            <Badge variant="outline" className="text-[10px]">{item.origen}</Badge>
            {(() => { const C = CLASE[item.clase]; return <Badge variant="outline" className={`text-[10px] ${C.cls}`}><C.icon className="mr-1 h-3 w-3" />{C.label}</Badge>; })()}
          </DialogTitle>
          <DialogDescription className="text-xs">{item.ref}{item.motivo ? ` · ${item.motivo}` : ""}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-0 lg:grid-cols-2">
          {/* PDF */}
          <div className="h-[55vh] border-b bg-muted/30 lg:h-[72vh] lg:border-b-0 lg:border-r" data-testid="reviewer-pdf">
            {pdfErr ? (
              <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">No se pudo cargar el PDF.</div>
            ) : (
              <PdfViewer data={pdfData} loading={!pdfData} />
            )}
          </div>

          {/* Editor */}
          <div className="max-h-[55vh] overflow-y-auto p-4 lg:max-h-[72vh]">
            <div className="rounded-lg border bg-zinc-50/60 p-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Datos de la factura</p>
              <div className="grid grid-cols-2 gap-2">
                {INV_FIELDS.map((f) => (
                  <div key={f.col} className={f.col === "Nombre del contacto" ? "col-span-2" : ""}>
                    <Label className="text-[11px]">{f.label}</Label>
                    <Input value={inv[f.col] || ""} onChange={(e) => setInv((p) => ({ ...p, [f.col]: e.target.value }))}
                      className="h-8 text-sm" data-testid={`inv-${f.col}`} />
                  </div>
                ))}
                <div className="col-span-2">
                  <Label className="text-[11px]">País (define la hoja Holded)</Label>
                  <Select value={pais} onValueChange={setPais}>
                    <SelectTrigger className="h-8 text-sm" data-testid="inv-pais"><SelectValue /></SelectTrigger>
                    <SelectContent>{paises.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Líneas Holded ({rows.length})</p>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addLine} data-testid="reviewer-add-line"><Plus className="mr-1 h-3 w-3" />Añadir línea</Button>
            </div>

            <div className="mt-2 space-y-2">
              {rows.map((r, idx) => {
                const issues = lineIssues(idx);
                return (
                  <div key={idx} className="rounded-lg border p-3" data-testid={`reviewer-line-${idx}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-medium text-muted-foreground">Línea {idx + 1}</span>
                      {rows.length > 1 && (
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => removeLine(idx)} data-testid={`reviewer-del-line-${idx}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      )}
                    </div>
                    <Input value={r["Concepto"] || ""} onChange={(e) => { setLine(idx, "Concepto", e.target.value); setLine(idx, "Descripción del producto", e.target.value); }}
                      placeholder="Concepto" className="mt-1 h-8 text-sm" data-testid={`line-concepto-${idx}`} />
                    <div className="mt-2 grid grid-cols-4 gap-2">
                      <div><Label className="text-[10px]">Base €</Label><Input value={r["Precio unidad"] || ""} onChange={(e) => setLine(idx, "Precio unidad", e.target.value)} className="h-8 text-sm tabular-nums" data-testid={`line-base-${idx}`} /></div>
                      <div><Label className="text-[10px]">IVA %</Label><Input value={r["IVA %"] ?? ""} onChange={(e) => setLine(idx, "IVA %", e.target.value)} className="h-8 text-sm tabular-nums" data-testid={`line-iva-${idx}`} /></div>
                      <div><Label className="text-[10px]">Ret. %</Label><Input value={r["Retención %"] ?? ""} onChange={(e) => setLine(idx, "Retención %", e.target.value)} className="h-8 text-sm tabular-nums" data-testid={`line-ret-${idx}`} /></div>
                      <div>
                        <Label className="text-[10px]">ISP</Label>
                        <Select value={String(r["Inv. Suj. Pasivo (1/0)"] || "0")} onValueChange={(v) => setLine(idx, "Inv. Suj. Pasivo (1/0)", v)}>
                          <SelectTrigger className="h-8 text-sm" data-testid={`line-isp-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="0">No</SelectItem><SelectItem value="1">Sí</SelectItem></SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="mt-2">
                      <Label className="flex items-center gap-1 text-[10px]"><Landmark className="h-3 w-3" />Cuenta de gasto (Holded)</Label>
                      <Select value={r["Num. Cuenta de gasto"] ? String(r["Num. Cuenta de gasto"]) : NONE} onValueChange={(v) => setCuenta(idx, v)}>
                        <SelectTrigger className="h-8 text-sm" data-testid={`line-cuenta-${idx}`}><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          <SelectItem value={NONE}>— Sin asignar —</SelectItem>
                          {cuentas.map((c) => <SelectItem key={c.numero} value={c.numero}><span className="font-mono">{c.numero}</span> · {c.nombre}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {issues.length > 0 && (
                      <p className="mt-1.5 flex items-center gap-1 text-[11px] text-rose-600"><AlertTriangle className="h-3 w-3" />{issues.join(" · ")}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-row items-center justify-between gap-3 border-t px-5 py-3 sm:justify-between">
          <label className="flex cursor-pointer items-center gap-2 text-sm" data-testid="reviewer-revisada-wrap">
            <Checkbox checked={revisada} onCheckedChange={(v) => setRevisada(!!v)} data-testid="reviewer-revisada" />
            <span className="flex items-center gap-1"><BadgeCheck className="h-4 w-4 text-emerald-600" />Marcar como <strong>revisada</strong> (incluir en el cierre)</span>
          </label>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} data-testid="reviewer-cancel">Cancelar</Button>
            <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid="reviewer-save">
              {saveMut.isPending ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Guardando…</> : <><Save className="mr-1.5 h-4 w-4" />Guardar</>}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevItem({ it, onOpen, onRetry, retrying }) {
  const C = CLASE[it.clase];
  const canRetry = it.clase === "pendiente" && it.file_id && it.owner_kind === "reserva" && (it.motivo || "").startsWith("Error de lectura");
  return (
    <Card data-testid={`rev-item-${it.uid}`}><CardContent className="flex flex-wrap items-center justify-between gap-3 p-3">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className={`text-[10px] ${C.cls}`}><C.icon className="mr-1 h-3 w-3" />{C.label}</Badge>
          <span className="flex items-center gap-1 truncate text-sm font-medium"><FileText className="h-3.5 w-3.5 text-muted-foreground" />{it.filename}</span>
          {it.revisada && <Badge className="bg-emerald-600 text-[10px] text-white"><BadgeCheck className="mr-1 h-3 w-3" />Revisada</Badge>}
        </div>
        <p className="truncate text-xs text-muted-foreground">{it.origen} · {it.ref} · {it.pais} · {it.lines.length} línea(s){it.motivo ? ` · ${it.motivo}` : ""}</p>
      </div>
      <div className="flex items-center gap-2">
        {it.issues_count > 0 && <Badge variant="outline" className="border-rose-200 bg-rose-50 text-[10px] text-rose-700"><AlertTriangle className="mr-1 h-3 w-3" />{it.issues_count}</Badge>}
        {canRetry && (
          <Button size="sm" variant="outline" onClick={() => onRetry(it)} disabled={retrying} data-testid={`rev-retry-${it.uid}`}>
            {retrying ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RotateCw className="mr-1.5 h-4 w-4" />}Reintentar lectura
          </Button>
        )}
        <Button size="sm" onClick={onOpen} data-testid={`rev-edit-${it.uid}`}><Eye className="mr-1.5 h-4 w-4" />Revisar / editar</Button>
      </div>
    </CardContent></Card>
  );
}

export default function CierreRevision() {
  const qc = useQueryClient();
  const [paisFilter, setPaisFilter] = useState("Todos");
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [selected, setSelected] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  const revQ = useQuery({ queryKey: ["conta-revision"], queryFn: async () => (await api.get("/contabilidad/revision")).data });
  const cuentasQ = useQuery({ queryKey: ["conta-cuentas"], queryFn: async () => (await api.get("/contabilidad/cuentas")).data });

  const data = revQ.data;
  const items = data?.items || [];
  const paisesPresentes = useMemo(() => ["Todos", ...Array.from(new Set(items.map((i) => i.pais)))], [items]);
  const filtered = items.filter((i) =>
    (paisFilter === "Todos" || i.pais === paisFilter) &&
    (!onlyIssues || i.issues_count > 0 || i.clase === "pendiente")
  );

  // Re-lectura individual (para fallos puntuales de OCR en facturas de SOFI 2.0)
  const retryMut = useMutation({
    mutationFn: async (it) => (await api.post("/contabilidad/reextraer", { owner_kind: it.owner_kind, owner_id: it.owner_id, factura_id: it.factura_id })).data,
    onMutate: (it) => setRetryingId(it.uid),
    onSuccess: (res) => {
      if (res.estado === "extraida") toast.success(`Factura leída · ${res.n_rows} línea(s)`);
      else if (res.estado === "proforma") toast.message("Es una proforma (no se contabiliza)");
      else toast.error(`No se pudo leer: ${res.error || res.estado}`);
      qc.invalidateQueries({ queryKey: ["conta-revision"] });
      qc.invalidateQueries({ queryKey: ["conta-preview"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
    onSettled: () => setRetryingId(null),
  });

  const weeks = useMemo(() => {
    const byWeek = {};
    filtered.forEach((it) => { const w = it.semana || "Sin fecha"; (byWeek[w] ||= []).push(it); });
    return Object.entries(byWeek)
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([week, its]) => {
        const ret = its.filter((i) => i.clase === "retencion");
        const nonRet = its.filter((i) => i.clase !== "retencion");
        const paises = {};
        nonRet.forEach((i) => { const p = i.pais || "Sin país"; (paises[p] ||= []).push(i); });
        const paisBlocks = Object.entries(paises).sort((a, b) => {
          const ia = COUNTRY_ORDER.indexOf(a[0]); const ib = COUNTRY_ORDER.indexOf(b[0]);
          return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
        });
        const pendientes = its.filter((i) => i.clase === "pendiente").length;
        return { week, items: its, paisBlocks, ret, pendientes };
      });
  }, [filtered]);

  const onSaved = () => {
    setSelected(null);
    qc.invalidateQueries({ queryKey: ["conta-revision"] });
    qc.invalidateQueries({ queryKey: ["conta-preview"] });
  };

  const c = data?.counts || {};

  return (
    <div className="space-y-4" data-testid="conta-revision">
      <Card><CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-800"><FileSpreadsheet className="mr-1 h-3 w-3" />{c.holded || 0} Holded</Badge>
          <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800"><ReceiptText className="mr-1 h-3 w-3" />{c.retencion || 0} retención</Badge>
          <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-800"><AlertTriangle className="mr-1 h-3 w-3" />{c.pendientes || 0} pendientes</Badge>
          {data?.week_label && <span className="text-xs text-muted-foreground">semana {data.week_label}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={paisFilter} onValueChange={setPaisFilter}>
            <SelectTrigger className="h-8 w-40 text-sm" data-testid="rev-pais-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{paisesPresentes.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
          <label className="flex cursor-pointer items-center gap-1.5 text-xs">
            <Checkbox checked={onlyIssues} onCheckedChange={(v) => setOnlyIssues(!!v)} data-testid="rev-only-issues" />Solo con incidencias
          </label>
          <Button variant="outline" size="sm" onClick={() => revQ.refetch()} data-testid="rev-refresh"><RefreshCw className="mr-1.5 h-4 w-4" />Actualizar</Button>
        </div>
      </CardContent></Card>

      {revQ.isLoading ? <Skeleton className="h-40 w-full" /> : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground" data-testid="rev-empty">
          No hay facturas abiertas para revisar esta semana. Las facturas ya cerradas no aparecen aquí.
        </CardContent></Card>
      ) : (
        <Accordion type="multiple" defaultValue={weeks.map((w) => w.week)} className="space-y-2" data-testid="rev-weeks">
          {weeks.map((wk) => (
            <AccordionItem key={wk.week} value={wk.week} className="rounded-xl border bg-card px-4" data-testid={`rev-week-${wk.week}`}>
              <AccordionTrigger className="hover:no-underline">
                <span className="flex flex-wrap items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-primary" />
                  <span className="font-semibold">Semana {wk.week}</span>
                  <Badge variant="outline" className="text-[10px]">{wk.items.length} factura(s)</Badge>
                  {wk.pendientes > 0 && <Badge variant="outline" className="border-rose-200 bg-rose-50 text-[10px] text-rose-700">{wk.pendientes} pendientes</Badge>}
                  {wk.ret.length > 0 && <Badge variant="outline" className="border-amber-200 bg-amber-50 text-[10px] text-amber-700">{wk.ret.length} retención</Badge>}
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-4">
                {wk.paisBlocks.map(([pais, its]) => (
                  <div key={pais} data-testid={`rev-pais-${wk.week}-${pais}`}>
                    <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      <Globe2 className="h-3.5 w-3.5" />{pais} <span className="font-normal normal-case">· {its.length}</span>
                    </p>
                    <div className="space-y-2">{its.map((it) => <RevItem key={it.uid} it={it} onOpen={() => setSelected(it)} onRetry={(x) => retryMut.mutate(x)} retrying={retryingId === it.uid} />)}</div>
                  </div>
                ))}
                {wk.ret.length > 0 && (
                  <div data-testid={`rev-ret-${wk.week}`}>
                    <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700">
                      <ReceiptText className="h-3.5 w-3.5" />Retención (IRPF) <span className="font-normal normal-case text-muted-foreground">· {wk.ret.length}</span>
                    </p>
                    <div className="space-y-2">{wk.ret.map((it) => <RevItem key={it.uid} it={it} onOpen={() => setSelected(it)} onRetry={(x) => retryMut.mutate(x)} retrying={retryingId === it.uid} />)}</div>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {selected && cuentasQ.data && (
        <InvoiceReviewer
          item={selected}
          cuentas={cuentasQ.data.cuentas || []}
          paises={cuentasQ.data.paises || []}
          columns={data?.columns || cuentasQ.data.columns || []}
          onClose={() => setSelected(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
